import {
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { v2MenuItemDangerClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Modals, useShowModals } from 'src/store/modals';
import { useDisplayTime } from 'src/utils/hooks';
import { FileTypeBadge } from './file-type-badge';

type FileListItemProps = {
  disabled?: boolean;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  fileType: string;
  pinnedAt?: string | null;
  onPin: () => void;
  onUnpin: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onOpen: () => void;
  onRename: (newName: string) => void;
};

export const FileListItem = ({
  disabled,
  fileName,
  uploadedBy,
  uploadedAt,
  fileType,
  pinnedAt,
  onPin,
  onUnpin,
  onDelete,
  onDownload,
  onOpen,
  onRename,
}: FileListItemProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [showAddressFileRenameModal] = useShowModals(Modals.AddressFileRename);

  const { displayTime } = useDisplayTime();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (callback: () => void) => {
    callback();
    handleMenuClose();
  };

  return (
    <ListItem
      divider
      data-testid="address-file-item"
      sx={{
        border: '1px solid',
        borderColor: pinnedAt ? 'warning.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        py: 0.25,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      secondaryAction={
        disabled ? null : (
          <>
            <IconButton onClick={handleMenuOpen} data-testid="address-file-menu">
              <DynamicIcon name="more-vertical" library="lucide" />
            </IconButton>
            <V2ThemeProvider>
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {pinnedAt ? (
                  <MenuItem onClick={() => handleAction(onUnpin)}>
                    <ListItemIcon>
                      <DynamicIcon name="pin" library="lucide" />
                    </ListItemIcon>
                    <ListItemText>Unpin</ListItemText>
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => handleAction(onPin)}>
                    <ListItemIcon>
                      <DynamicIcon name="pin" library="lucide" />
                    </ListItemIcon>
                    <ListItemText>Pin</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  data-testid="address-file-rename"
                  onClick={() =>
                    handleAction(() => showAddressFileRenameModal({ fileName, onRename }))
                  }
                >
                  <ListItemIcon>
                    <DynamicIcon name="pencil" library="lucide" />
                  </ListItemIcon>
                  <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleAction(onDownload)}>
                  <ListItemIcon>
                    <DynamicIcon name="download" library="lucide" />
                  </ListItemIcon>
                  <ListItemText>Download</ListItemText>
                </MenuItem>
                <MenuItem
                  className={v2MenuItemDangerClass}
                  onClick={() => handleAction(onDelete)}
                >
                  <ListItemIcon>
                    <DynamicIcon name="trash-2" library="lucide" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </Menu>
            </V2ThemeProvider>
          </>
        )
      }
    >
      <Box
        onClick={onOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <ListItemAvatar sx={{ minWidth: 'auto', mr: 2 }}>
          <FileTypeBadge type={fileType} />
        </ListItemAvatar>

        <ListItemText
          sx={{ pr: 2 }}
          primary={
            <Typography
              component="span"
              variant="body1"
              noWrap
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              <Box
                component="span"
                title={fileName}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fileName}
              </Box>
              {pinnedAt ? (
                <DynamicIcon
                  name="pin"
                  library="lucide"
                  fontSize="small"
                  sx={{ color: 'action.active', ml: 0.5 }}
                />
              ) : null}
            </Typography>
          }
          secondary={
            <Typography
              color="text.secondary"
              title={`${uploadedBy} • ${dayjs(uploadedAt).format('MMM D, YYYY')} ${displayTime(uploadedAt)}`}
              sx={{
                lineHeight: 1.5,
                fontSize: (theme) => theme.utils.important('0.75rem'),
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {uploadedBy} &bull; {dayjs(uploadedAt).format('MMM D, YYYY')}{' '}
              {displayTime(uploadedAt)}
            </Typography>
          }
        />
      </Box>
    </ListItem>
  );
};
